import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform, useInView } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Wheelchair,
  Hospital,
  FirstAid,
  Heartbeat,
  Handshake,
  Shield,
  ShieldCheck,
  Clock,
  Path,
  UsersThree,
  Buildings,
  Truck,
  Phone,
  EnvelopeSimple,
  MapPin,
  CheckCircle,
  Pulse,
  DotsSixVertical,
  Student,
  Briefcase,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import { autoPrefixUS } from "../components/PhoneInput";
import MedexOVA from "../components/MedexOVA";
import CinematicHero from "../components/marketing/CinematicHero";

const taglines = [
  "NEMT Facilitator. Nationwide, Autonomous-Ready Network.",
  "Moving Patients Forward—Safely, Efficiently, and On Time.",
  "Where Care Meets Next-Generation Mobility.",
  "Facilitation Expertise. OVA's Autonomous Fleet Technology.",
  "Connecting Patients to Care—One Trip at a Time.",
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1762135995921-fedb142e7b96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHx3aGl0ZSUyMGRlbGl2ZXJ5JTIwdmFufGVufDB8fHx8MTc3NjY1NzU1OXww&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG =
  "https://images.pexels.com/photos/127873/pexels-photo-127873.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const WHEELCHAIR_IMG =
  "https://images.pexels.com/photos/35831410/pexels-photo-35831410.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PARTNERSHIP_IMG =
  "https://images.unsplash.com/photo-1758518730384-be3d205838e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxoYW5kc2hha2UlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzc2NjU3NTU1fDA&ixlib=rb-4.1.0&q=85";

/* Reusable scroll reveal */
function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* Animated counter */
function Counter({ value, suffix = "", duration = 1.6 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const from = 0;
    const to = Number(value) || 0;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);
  return <span ref={ref}>{n}{suffix}</span>;
}

/* ---------------- Hero ---------------- */
function Hero() {
  const [idx, setIdx] = useState(0);
  const heroRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const orbX = useTransform(sx, (v) => v * 20);
  const orbY = useTransform(sy, (v) => v * 20);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % taglines.length), 4200);
    return () => clearInterval(t);
  }, []);

  const onMove = (e) => {
    const r = heroRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={heroRef} onMouseMove={onMove} className="relative overflow-hidden bg-bone" data-testid="hero-section">
      {/* Ambient orbs */}
      <motion.div
        aria-hidden
        className="orb h-72 w-72 bg-brandblue/35 top-20 -left-10"
        style={{ x: orbX, y: orbY }}
      />
      <motion.div
        aria-hidden
        className="orb h-80 w-80 bg-ova/25 -bottom-20 right-10"
        style={{ x: orbX, y: orbY }}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10 pt-16 pb-24 lg:pt-24 lg:pb-32">
        <div className="lg:col-span-7 flex flex-col">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-brandblue/20 bg-white/70 backdrop-blur px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold text-brandblue-deep w-fit">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-brandblue/60 live-dot"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-brandblue"></span>
              </span>
              NEMT Facilitator · Nationwide Network
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-slate_navy font-black">
              Reliable.
              <br />
              Compliant.
              <br />
              <span className="shimmer-text">Connected.</span>
            </h1>
          </Reveal>
          <div className="mt-6 min-h-[64px] max-w-xl">
            <AnimatePresence mode="wait">
              <motion.p
                key={idx}
                initial={{ y: 14, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -14, opacity: 0 }}
                transition={{ duration: 0.45 }}
                className="text-lg text-slate-600 font-medium italic"
                data-testid="hero-rotating-tagline"
              >
                “{taglines[idx]}”
              </motion.p>
            </AnimatePresence>
          </div>
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-xl text-base text-slate-600 leading-relaxed">
              Medex Transportation Services LLC is an <strong>NEMT facilitator</strong>, coordinating
              non-emergency medical transport across the United States. Our partnership with{" "}
              <span className="text-ova font-semibold">OVA</span> — a nationwide, <strong>fully autonomous rideshare platform</strong> —
              pairs that facilitation expertise with next-generation vehicle technology, a combination
              no traditional NEMT provider can match, and one that's expanding into new markets every quarter.
            </p>
          </Reveal>
          <Reveal delay={0.22}>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/book"
                data-testid="hero-book-trip-btn"
                className="inline-flex items-center gap-2 rounded-md bg-brandblue-ink hover:bg-brandblue-deep text-white px-6 py-3 text-sm font-semibold transition-all hover:-translate-y-0.5 shadow-[0_10px_24px_-12px_rgba(11,27,51,0.6)]"
              >
                Request a Trip <ArrowRight weight="bold" size={14} />
              </Link>
              <Link
                to="/ride/new"
                data-testid="hero-hail-ride-btn"
                className="inline-flex items-center gap-2 rounded-md bg-forest hover:bg-forest-hover text-white px-6 py-3 text-sm font-semibold transition-all hover:-translate-y-0.5 shadow-[0_10px_24px_-12px_rgba(43,76,59,0.5)]"
              >
                Hail a ride now <ArrowRight weight="bold" size={14} />
              </Link>
              <a
                href="#services"
                data-testid="hero-services-btn"
                className="inline-flex items-center gap-2 rounded-md border border-slate_navy/10 text-slate_navy bg-white hover:bg-slate-50 px-6 py-3 text-sm font-semibold transition-colors"
              >
                Our Services <ArrowUpRight size={14} />
              </a>
              <a
                href="tel:+12544671577"
                data-testid="hero-dispatch-line-link"
                className="inline-flex items-center gap-2 text-sm text-slate-500 pl-2 hover:text-brandblue transition-colors"
              >
                <Phone size={16} className="text-brandblue" /> Dispatch line: +1 (254) 467-1577
              </a>
            </div>
          </Reveal>

          {/* Trust stats with animated counters */}
          <Reveal delay={0.3}>
            <div className="mt-12 grid grid-cols-3 max-w-xl border-t border-[#E2E0D8]">
              {[
                { k: <Counter value={95} suffix="%" />, v: "On-time arrival" },
                { k: <Counter value={99} suffix=".9%" />, v: "Safety record" },
                { k: <><Counter value={10} />+</>, v: "Years experience" },
              ].map((s, i) => (
                <div key={i} className={`py-5 ${i !== 0 ? "border-l border-[#E2E0D8] pl-5" : ""}`}>
                  <div className="font-heading text-3xl sm:text-4xl font-bold text-slate_navy">{s.k}</div>
                  <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500">{s.v}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <motion.div
          className="lg:col-span-5 relative"
          style={{ x: useTransform(sx, (v) => v * -8), y: useTransform(sy, (v) => v * -8) }}
        >
          <div className="relative h-[480px] lg:h-full rounded-lg overflow-hidden border border-[#E2E0D8] bg-brandblue-soft">
            <img src={HERO_IMG} alt="MedexOVA Medical Transport Van" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute bottom-4 left-4 right-4 bg-white/92 backdrop-blur-md border border-black/5 rounded-md p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-md bg-brandblue-ink text-white inline-flex items-center justify-center">
                  <Truck weight="duotone" size={22} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-brandblue-deep font-bold">Fleet coordination</div>
                  <div className="text-sm text-slate_navy font-medium">Ambulatory · Wheelchair · Stretcher capable</div>
                </div>
              </div>
            </div>
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-white/92 backdrop-blur-md border border-black/5 rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.2em] font-bold">
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-forest"></span>
                <span className="h-2 w-2 rounded-full bg-ova"></span>
              </span>
              Powered by <MedexOVA className="ml-1" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* partner marquee */}
      <div className="border-y border-[#E2E0D8] bg-white/60 overflow-hidden">
        <div className="flex marquee-track gap-14 py-5 whitespace-nowrap text-xs uppercase tracking-[0.3em] text-slate-500 font-semibold">
          {[...Array(2)].flatMap((_, n) =>
            ["HOSPITALS","·","DIALYSIS CENTERS","·","NURSING HOMES","·","ASSISTED LIVING","·","VA · VETERAN ORGS","·","LONG-TERM CARE","·","CLINICS","·","SCHOOLS","·","PERSONAL INJURY","·","PRIVATE PATIENTS","·"].map((t, i) => (
              <span key={`${n}-${i}`} className="shrink-0">{t}</span>
            ))
          )}
        </div>
      </div>
    </section>
  );
}

/* ---------------- About ---------------- */
function About() {
  const commitments = [
    { icon: Clock, label: "Timeliness & reliability" },
    { icon: Shield, label: "Patient dignity & safety" },
    { icon: CheckCircle, label: "Regulatory compliance" },
    { icon: Handshake, label: "Strong provider partnerships" },
  ];
  return (
    <section id="about" className="py-24 sm:py-32 bg-bone" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-brandblue flex items-center gap-2">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-brandblue/60 live-dot"/><span className="relative inline-flex h-2 w-2 rounded-full bg-brandblue"/></span>
              About MedexOVA
            </div>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl tracking-tight font-bold text-slate_navy leading-tight">
              An NEMT facilitator, powered by next-generation autonomous technology.
            </h2>
            <p className="mt-6 text-base text-slate-600 leading-relaxed">
              Medex Transportation Services LLC is an <strong>NEMT facilitator</strong>, coordinating trips between
              healthcare providers and a vetted nationwide carrier network. Our partnership with{" "}
              <span className="text-ova font-semibold">OVA</span> — a nationwide, fully autonomous rideshare
              platform — layers cutting-edge autonomous vehicle technology on top of that network, giving us
              a market advantage no traditional facilitator has, in every market we enter.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 grid grid-cols-2 gap-px bg-[#E2E0D8] border border-[#E2E0D8] rounded-md overflow-hidden">
              {commitments.map(({ icon: Icon, label }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -2 }}
                  className="bg-white p-5 flex items-start gap-3 hover:bg-brandblue-soft/30 transition-colors"
                >
                  <Icon weight="duotone" size={22} className="text-brandblue" />
                  <span className="text-sm font-medium text-slate_navy">{label}</span>
                </motion.div>
              ))}
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <motion.div
              whileHover={{ y: -4, boxShadow: "0 20px 60px -20px rgba(15,23,42,0.35)" }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-lg overflow-hidden border border-[#E2E0D8] aspect-[4/3] lg:aspect-auto lg:h-full">
              <img src={ABOUT_IMG} alt="Healthcare facility corridor" className="h-full w-full object-cover" />
              <div className="absolute bottom-4 right-4 left-4 md:left-auto md:w-80 bg-white/95 backdrop-blur-md border border-black/5 rounded-md p-5">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-brandblue-deep mb-2">
                  <ShieldCheck size={14} weight="fill" className="text-brandblue" /> HIPAA Compliant
                </div>
                <p className="text-sm text-slate_navy leading-relaxed">
                  Every trip handled with precision, empathy, and full operational transparency —
                  from pickup to drop-off.
                </p>
              </div>
            </motion.div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Services ---------------- */
function Services() {
  const categories = [
    {
      tag: "01 — NEMT",
      title: "Non-Emergency Medical Transportation",
      copy: "End-to-end patient transport for scheduled medical needs. Ambulatory, wheelchair and stretcher-capable vehicles operated by trained, background-checked drivers.",
      bullets: ["Ambulatory transport","Wheelchair transport","Facility-to-facility transfers","Dialysis appointments","Hospital discharges"],
      icon: Wheelchair,
      image: WHEELCHAIR_IMG,
    },
    {
      tag: "02 — Coordination",
      title: "Medical Transportation Coordination",
      copy: "A central coordination hub connecting healthcare facilities with qualified transportation providers. We handle proper trip classification, fair pricing, and full trip transparency.",
      bullets: ["Proper trip classification","Fair & accurate pricing","Reduced wait times","Full trip transparency"],
      icon: Path,
    },
    {
      tag: "03 — Facilitation",
      title: "Facilitation & Enterprise Logistics",
      copy: null, // custom-rendered to embed MedexOVA two-toned
      bullets: ["High-volume transport coordination","Multi-provider logistics","Healthcare compliance","Scalable for enterprise care"],
      icon: Handshake,
      accent: true,
    },
  ];

  const additional = [
    { icon: Student, title: "School Transportation", copy: "Dependable, student-first routing — including support for students experiencing homelessness." },
    { icon: Briefcase, title: "Personal Injury Transport", copy: "Compassionate rides to and from appointments for clients recovering from accidents." },
  ];

  return (
    <section id="services" className="py-24 sm:py-32 bg-white border-y border-[#E2E0D8]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-brandblue flex items-center gap-2">
                <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-brandblue/60 live-dot"/><span className="relative inline-flex h-2 w-2 rounded-full bg-brandblue"/></span>
                What we do
              </div>
              <h2 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight font-bold text-slate_navy leading-tight max-w-2xl">
                Three connected capabilities. One coordinated experience.
              </h2>
            </div>
            <Link to="/book" className="inline-flex items-center gap-2 text-sm font-semibold text-brandblue hover:text-brandblue-deep" data-testid="services-book-link">
              Request a trip <ArrowUpRight size={14} />
            </Link>
          </div>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-3 border border-[#E2E0D8] rounded-lg overflow-hidden">
          {categories.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.title}
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -4 }}
                className={`p-8 flex flex-col gap-5 ${i !== 2 ? "md:border-r border-[#E2E0D8]" : ""} ${
                  c.accent ? "bg-gradient-to-br from-forest to-brandblue-ink text-white" : "bg-white hover:bg-brandblue-soft/20"
                } transition-colors`}
              >
                <div className="flex items-center justify-between">
                  <div className={`h-12 w-12 rounded-md inline-flex items-center justify-center ${c.accent ? "bg-white/10 text-white" : "bg-brandblue-soft text-brandblue-deep"}`}>
                    <Icon weight="duotone" size={24} />
                  </div>
                  <div className={`text-[11px] uppercase tracking-[0.2em] font-bold ${c.accent ? "text-white/80" : "text-brandblue"}`}>
                    {c.tag}
                  </div>
                </div>
                <h3 className={`font-heading text-xl sm:text-2xl leading-tight font-semibold ${c.accent ? "text-white" : "text-slate_navy"}`}>
                  {c.title}
                </h3>
                {c.accent ? (
                  <p className="text-sm leading-relaxed text-white/85">
                    As{" "}
                    <span className="font-semibold whitespace-nowrap">
                      <span className="text-[#16A34A]">Medex</span>
                      <span className="text-[#C4B5FD]">OVA</span>
                    </span>
                    , we pair Medex's NEMT facilitation with OVA's nationwide, fully autonomous rideshare
                    technology to coordinate high-volume transport, manage logistics across providers, and
                    deliver scalable solutions for hospitals, clinics, and long-term care.
                  </p>
                ) : (
                  <p className="text-sm leading-relaxed text-slate-600">{c.copy}</p>
                )}
                <ul className="mt-auto space-y-2">
                  {c.bullets.map((b) => (
                    <li key={b} className={`flex items-start gap-2 text-sm ${c.accent ? "text-white/90" : "text-slate_navy"}`}>
                      <DotsSixVertical size={16} className={c.accent ? "text-white/60" : "text-brandblue"} />
                      {b}
                    </li>
                  ))}
                </ul>
                {c.image && (
                  <div className="mt-4 rounded-md overflow-hidden border border-[#E2E0D8] aspect-[16/10]">
                    <img src={c.image} alt={c.title} className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Additional specialty transport strip */}
        <Reveal delay={0.1}>
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {additional.map(({ icon: Icon, title, copy }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -3 }}
                className="group flex items-start gap-4 p-5 rounded-lg border border-[#E2E0D8] bg-bone hover:bg-white hover:shadow-md transition-all"
              >
                <div className="h-11 w-11 rounded-md bg-brandblue-soft text-brandblue-deep inline-flex items-center justify-center group-hover:bg-brandblue group-hover:text-white transition-colors">
                  <Icon weight="duotone" size={22} />
                </div>
                <div>
                  <div className="font-heading text-base font-semibold text-slate_navy">{title}</div>
                  <p className="mt-1 text-sm text-slate-600">{copy}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Who We Serve ---------------- */
function WhoWeServe() {
  const items = [
    { icon: Hospital, label: "Hospitals", desc: "Coordinated discharge & inter-facility transfers." },
    { icon: Buildings, label: "Nursing homes & assisted living", desc: "Recurring appointment routing with full reporting." },
    { icon: Heartbeat, label: "Dialysis centers", desc: "On-time, standing order dialysis transport." },
    { icon: Shield, label: "VA & veteran service orgs", desc: "Compliant, respectful mobility for veterans." },
    { icon: UsersThree, label: "Private patients & families", desc: "Simple booking for individual, scheduled trips." },
    { icon: FirstAid, label: "Clinics & long-term care", desc: (<>Enterprise logistics through <MedexOVA />.</>) },
  ];
  return (
    <section id="who-we-serve" className="py-24 sm:py-32 bg-bone" data-testid="who-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal>
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-6">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-brandblue flex items-center gap-2">
                <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-brandblue/60 live-dot"/><span className="relative inline-flex h-2 w-2 rounded-full bg-brandblue"/></span>
                Who we serve
              </div>
              <h2 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight font-bold text-slate_navy leading-tight">
                Built for the people who keep patients moving.
              </h2>
            </div>
            <p className="lg:col-span-6 text-base text-slate-600 leading-relaxed">
              From individual appointments to enterprise-scale facility logistics, MedexOVA
              coordinates reliable non-emergency medical transportation nationwide — with a single
              point of accountability and a single invoice.
            </p>
          </div>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 border border-[#E2E0D8] bg-white rounded-lg overflow-hidden">
          {items.map(({ icon: Icon, label, desc }, i) => (
            <motion.div
              key={label}
              data-testid={`serve-card-${i}`}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -3 }}
              className="p-8 border-b last:border-b-0 sm:[&:nth-child(2n)]:border-r-0 sm:border-r lg:[&:nth-child(3n)]:border-r-0 sm:[&:nth-last-child(-n+2)]:border-b-0 lg:[&:nth-last-child(-n+3)]:border-b-0 border-[#E2E0D8] hover:bg-brandblue-soft/40 transition-colors"
            >
              <Icon weight="duotone" size={28} className="text-brandblue" />
              <h3 className="mt-4 font-heading text-lg font-semibold text-slate_navy">{label}</h3>
              <p className="mt-2 text-sm text-slate-600">{desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Why Choose ---------------- */
function WhyChoose() {
  const reasons = [
    "Experienced in medical transportation logistics",
    "Strong nationwide provider network",
    <>Partnership with <span className="text-ova font-semibold">OVA</span> — a nationwide, fully autonomous rideshare platform</>,
    "Relentless focus on compliance & trip classification",
    "HIPAA-compliant, background-checked drivers",
    "Dedicated customer service and coordination",
  ];
  return (
    <section className="relative py-24 sm:py-32 bg-slate_navy text-white overflow-hidden" data-testid="why-section">
      <div aria-hidden className="orb h-96 w-96 bg-brandblue/20 -top-20 -left-20" />
      <div aria-hidden className="orb h-80 w-80 bg-ova/20 bottom-0 right-0" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-brandblue">Why MedexOVA</div>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight font-bold leading-tight">
              Coordinated care, in motion.
            </h2>
            <p className="mt-6 text-base text-white/70 leading-relaxed max-w-lg">
              We combine the precision of NEMT facilitation with the reach of OVA's fully autonomous
              rideshare technology — so your patients, staff, and billing stay aligned, trip after trip.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <ul className="divide-y divide-white/10 border-y border-white/10">
            {reasons.map((r, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="grid grid-cols-[60px_1fr_24px] items-center py-5 hover:bg-white/5 transition-colors group"
                data-testid={`why-row-${i}`}
              >
                <div className="font-heading text-white/40 group-hover:text-brandblue transition-colors">0{i + 1}</div>
                <div className="text-base sm:text-lg font-medium text-white">{r}</div>
                <ArrowUpRight size={18} className="text-brandblue group-hover:translate-x-1 transition-transform" />
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Partnership ---------------- */
function Partnership() {
  return (
    <section id="partnership" className="py-24 sm:py-32 bg-bone" data-testid="partnership-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6">
          <motion.div
            whileHover={{ y: -4, boxShadow: "0 20px 60px -20px rgba(15,23,42,0.35)" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-lg overflow-hidden border border-[#E2E0D8] aspect-[4/3]">
            <img src={PARTNERSHIP_IMG} alt="MedexOVA Partnership Handshake" className="h-full w-full object-cover" />
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-white/95 backdrop-blur-md border border-black/5 rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.2em] font-bold">
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-forest"></span>
                <span className="h-2 w-2 rounded-full bg-ova"></span>
              </span>
              Powered by <MedexOVA className="ml-1" />
            </div>
          </motion.div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-6">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brandblue flex items-center gap-2">
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-brandblue/60 live-dot"/><span className="relative inline-flex h-2 w-2 rounded-full bg-brandblue"/></span>
            Partnership
          </div>
          <h2 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight font-bold text-slate_navy leading-tight">
            Two companies, one next-generation network.
          </h2>
          <p className="mt-6 text-base text-slate-600 leading-relaxed max-w-xl">
            Medex Transportation Services LLC is an independent <strong>NEMT facilitator</strong>.{" "}
            <span className="text-ova font-semibold">OVA</span> is a nationwide,{" "}
            <strong>fully autonomous rideshare platform</strong>. Together, as <MedexOVA className="inline" />, we deliver
            seamless, compliant, and scalable medical transportation — pairing trusted facilitation with
            next-generation autonomous vehicle technology across every U.S. market we cover, with new
            service areas coming online continuously.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-px bg-[#E2E0D8] border border-[#E2E0D8] rounded-md overflow-hidden">
            {[
              { t: "Medex role", d: "NEMT facilitation — coordination between facilities & providers", color: "forest" },
              { t: "OVA role", d: "Nationwide, fully autonomous rideshare technology", color: "ova" },
              { t: "Market advantage", d: "Next-gen autonomous fleet + healthcare-grade compliance", color: "brandblue" },
              { t: "Transparent delivery", d: "Clear classification & accurate billing", color: "forest" },
            ].map((x, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="bg-white p-5">
                <div className={`text-xs uppercase tracking-[0.2em] font-bold ${x.color === "ova" ? "text-ova" : x.color === "brandblue" ? "text-brandblue" : "text-forest"}`}>{x.t}</div>
                <div className="mt-1 text-sm text-slate_navy">{x.d}</div>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 rounded-xl border border-[#E2E0D8] bg-gradient-to-br from-white to-[#F9F8F6] p-6">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-forest">Join the MedexOVA network</div>
            <div className="mt-2 text-lg font-semibold text-slate_navy">Grow with us — as a Provider or a Driver.</div>
            <div className="mt-1 text-sm text-slate-600">
              Fleet operators plug in a full dispatch stack (POD, credentialing, invoicing) in days. Independent drivers get MedexOVA-credentialed once and paid weekly.
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <a href="/apply" data-testid="landing-cta-apply"
                className="inline-flex items-center gap-2 bg-forest text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-forest/90 transition">
                Join the network →
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Contact / CTA ---------------- */
function ContactCTA() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.post("/contact", form);
      toast.success("Message sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  const input = "w-full rounded-md border border-[#E2E0D8] bg-white px-4 py-3 text-sm text-slate_navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brandblue/25 focus:border-brandblue transition";

  return (
    <section id="contact" className="py-24 sm:py-32 bg-white border-t border-[#E2E0D8]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brandblue flex items-center gap-2">
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-brandblue/60 live-dot"/><span className="relative inline-flex h-2 w-2 rounded-full bg-brandblue"/></span>
            Get in touch
          </div>
          <h2 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight font-bold text-slate_navy leading-tight">
            Need reliable medical transportation?
          </h2>
          <p className="mt-5 text-base text-slate-600 leading-relaxed max-w-md">
            Partner with MedexOVA today and experience coordinated care in motion. Share a few details
            and our dispatch team will be in touch.
          </p>
          <div className="mt-8 space-y-4">
            {[
              { Icon: Phone, label: "Main line", value: "+1 (254) 467-1577", href: "tel:+12544671577" },
              { Icon: EnvelopeSimple, label: "General inquiries", value: "info@medexova.com", href: "mailto:info@medexova.com" },
              { Icon: EnvelopeSimple, label: "Partnerships & referrals", value: "Shanev@medexova.com", href: "mailto:Shanev@medexova.com" },
              { Icon: MapPin, label: "HQ", value: "Houston, TX · serving nationwide" },
            ].map(({ Icon, label, value, href }, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-brandblue-soft text-brandblue-deep inline-flex items-center justify-center">
                  <Icon size={18} weight="duotone" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</div>
                  {href ? (
                    <a href={href} className="text-base text-slate_navy font-medium hover:text-brandblue">{value}</a>
                  ) : (
                    <div className="text-base text-slate_navy font-medium">{value}</div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 p-6 rounded-lg border border-[#E2E0D8] bg-bone">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-brandblue">Facilities & coordinators</div>
            <p className="mt-2 text-sm text-slate-600">
              Scheduling a trip for a patient? Use the dedicated booking portal for complete trip
              details and confirmation.
            </p>
            <Link to="/book" data-testid="contact-book-trip-btn" className="mt-4 inline-flex items-center gap-2 rounded-md bg-brandblue-ink hover:bg-brandblue-deep text-white px-5 py-2.5 text-sm font-semibold">
              Open Booking Portal <ArrowRight weight="bold" size={14} />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} data-testid="contact-name-input" placeholder="Full name" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} data-testid="contact-email-input" placeholder="you@facility.com" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Phone</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                       onBlur={() => setForm((f) => ({ ...f, phone: autoPrefixUS(f.phone) }))}
                       className={input} data-testid="contact-phone-input" placeholder="(940) 000-0000" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Subject</label>
                <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={input} data-testid="contact-subject-input" placeholder="How can we help?" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Message</label>
                <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={input} data-testid="contact-message-input" placeholder="Share your needs, volume, or questions…" />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
              <p className="text-xs text-slate-500 max-w-md">
                By sending this message you agree to be contacted by Medex Transportation Services.
              </p>
              <button type="submit" disabled={submitting} data-testid="contact-submit-btn" className="inline-flex items-center gap-2 rounded-md bg-brandblue-ink hover:bg-brandblue-deep disabled:opacity-60 text-white px-6 py-3 text-sm font-semibold transition-all hover:-translate-y-0.5">
                {submitting ? "Sending…" : "Send Message"} <ArrowRight weight="bold" size={14} />
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main className="bg-bone">
      <CinematicHero />
      <Hero />
      <About />
      <Services />
      <WhoWeServe />
      <WhyChoose />
      <Partnership />
      <ContactCTA />
    </main>
  );
}
